import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";
import { Checkbox, VStack } from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { MemberSelfServiceConfigurationTable } from "../../components/MemberSelfServiceConfiguration/MemberSelfServiceConfigurationTable";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useToast } from "../../hooks/useToast";
import { PlusIcon } from "../../styles/icons";

export const MemberSelfServiceConfigurations = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const { showErrorToast, showSuccessToast } = useToast();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search member self-service configurations...",
  });

  const { checked: showDeleted, onChange: onChangeDeletedCheck } =
    useCheckboxState(false);

  const {
    items: configurations,
    refresh,
    isLoading,
  } = usePaginatedAdminApiRequest(
    (apiClient) =>
      apiClient.getMemberSelfServiceConfigurations(
        business.BusinessId as string,
        {
          q: debouncedQuery,
          includeDeleted: showDeleted ? "true" : "false",
        }
      ),
    (apiClient, _, continuationToken) =>
      apiClient.getMemberSelfServiceConfigurations(
        business.BusinessId as string,
        {
          q: debouncedQuery,
          includeDeleted: showDeleted ? "true" : "false",
          continuationToken,
        }
      ),
    [business.BusinessId, debouncedQuery, showDeleted]
  );

  const deleteRequest = useAdminApiUpdateRequest(
    (apiClient, id: string) =>
      apiClient.deleteMemberSelfServiceConfiguration(business.BusinessId!, id),
    [configurations]
  );

  const restoreRequest = useAdminApiUpdateRequest(
    (apiClient, id: string) =>
      apiClient.restoreMemberSelfServiceConfiguration(business.BusinessId!, id),
    [configurations]
  );

  const onClickNew = () =>
    navigate(
      `/admin/businesses/${business.BusinessId}/member-self-service-configurations/new`
    );

  const onClickEdit = (
    row: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/member-self-service-configurations/${row.Meta?.Id}`
    );

  const onClickDelete = useCallback(
    (row: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Delete Member Self-Service Configuration?",
        `Are you sure you wish to delete this configuration: ${row.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteRequest.request(row.Meta?.Id!);
        }
      }),
    [configurations]
  );

  const onClickRestore = useCallback(
    (row: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Restore Member Self-Service Configuration?",
        `Are you sure you wish to restore this deleted configuration: ${row.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreRequest.request(row.Meta?.Id!);
        }
      }),
    [configurations]
  );

  useEffect(() => {
    if (restoreRequest.result) {
      if (restoreRequest.result.ok) {
        showSuccessToast("Configuration successfully restored");
        refresh();
      } else {
        showErrorToast(
          restoreRequest.error ?? "Failed to restore configuration"
        );
      }
    }
  }, [restoreRequest.result, refresh]);

  useEffect(() => {
    if (deleteRequest.result) {
      if (deleteRequest.result.ok) {
        showSuccessToast("Configuration successfully deleted");
        refresh();
      } else {
        showErrorToast(deleteRequest.error ?? "Failed to delete configuration");
      }
    }
  }, [deleteRequest.result, refresh]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          Member Self-Service Configurations
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/member-self-service-configurations`}
              >
                Member Self-Service Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeleted}
                onChange={onChangeDeletedCheck}
                title="Include deleted configurations in the list."
              >
                Include deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNew}
              >
                New Configuration
              </BusinessMenu>
            </Box>
          </HStack>

          <MemberSelfServiceConfigurationTable
            configurations={configurations}
            isLoading={isLoading}
            disabledActions={false}
            debouncedQuery={debouncedQuery}
            onClickDelete={onClickDelete}
            onClickEdit={onClickEdit}
            onClickRestore={onClickRestore}
          />
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
